import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import leftIcon from '@/assets/chevron_left.svg';
import rightIcon from '@/assets/chevron_right.svg';
import { trackEvent } from '@/utils/googleAnalytics';
import { BannerSkeleton } from '@/components/Skeleton';

const INTERVAL_TIME = 10000;

const BannerSection = ({ banners, isLoading = false }) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const bannerCount = banners.length;
  const containerRef = useRef(null);
  const timerRef = useRef(null);
  const bannerWrapperRef = useRef(null);

  const scrollToIndex = useCallback((index) => {
    if (containerRef.current) {
      if (index < 0 || index >= bannerCount) {
        return;
      }
      const container = containerRef.current;
      const targetElement = container.children[index];
      if (targetElement) {
        const scrollLeft = index === 0 ? 0 : targetElement.offsetLeft;
        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    }
  }, [bannerCount]);

  const goToNext = useCallback(() => {
    if (bannerCount <= 1) {
      return;
    }
    setCurrentIndex((prev) => {
      const nextIndex = (prev + 1) % bannerCount;
      scrollToIndex(nextIndex);
      return nextIndex;
    });
  }, [bannerCount, scrollToIndex]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (bannerCount > 1) {
      timerRef.current = setInterval(goToNext, INTERVAL_TIME);
    }
  }, [bannerCount, goToNext]);

  const handlePrev = () => {
    if (bannerCount <= 1) {
      return;
    }
    setCurrentIndex((prev) => {
      const nextIndex = prev === 0 ? bannerCount - 1 : prev - 1;
      scrollToIndex(nextIndex);
      return nextIndex;
    });
    resetTimer();
  };

  const handleNext = () => {
    goToNext();
    resetTimer();
  };

  // Handle banner click based on action type
  const handleBannerClick = (banner) => {
    // Track banner click
    trackEvent('banner_click');

    if (!banner.action) return;

    switch (banner.action.type) {
      case 'LINK':
        // Open URL in new tab
        window.open(banner.action.param, '_blank');
        break;
      case 'SEARCH':
        // Navigate to search page with keyword
        navigate(`/search?q=${encodeURIComponent(banner.action.param)}`);
        break;
      case 'NONE':
      default:
        // Do nothing
        break;
    }
  };

  // Initialize timer
  useEffect(() => {
    if (bannerCount <= 1) {
      return;
    }
    timerRef.current = setInterval(goToNext, INTERVAL_TIME);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [bannerCount, goToNext]);

  useEffect(() => {
    if (bannerCount === 0) {
      setCurrentIndex(0);
      return;
    }
    if (currentIndex > bannerCount - 1) {
      setCurrentIndex(0);
      scrollToIndex(0);
    }
  }, [bannerCount, currentIndex, scrollToIndex]);

  // Prevent manual scrolling with wheel - use native addEventListener with passive: false
  useEffect(() => {
    const preventScroll = (e) => {
      e.preventDefault();
    };

    const wrapper = bannerWrapperRef.current;
    if (wrapper) {
      wrapper.addEventListener('wheel', preventScroll, { passive: false });
      return () => {
        wrapper.removeEventListener('wheel', preventScroll);
      };
    }
  }, []);

  if (isLoading) {
    return <BannerSkeleton />;
  }

  return (
    <div className="w-full flex-col justify-start items-start gap-6 inline-flex">
      <div className="relative w-full">
        <div 
          ref={bannerWrapperRef}
          className="overflow-hidden"
        >
          <div 
            ref={containerRef}
            className="flex w-full overflow-x-hidden scroll-smooth"
          >
            {banners.map((banner, index) => (
              <div
                key={index}
                className="relative flex-shrink-0 w-full min-w-full"
              >
                <div
                  className={`relative rounded-md overflow-hidden w-full max-h-[360px] ${
                    banner.action && banner.action.type !== 'NONE' ? 'cursor-pointer' : ''
                  }`}
                  onClick={() => handleBannerClick(banner)}
                  style={{ aspectRatio: '1408 / 360' }}
                >
                  <img
                    src={banner.image}
                    alt={banner.title}
                    className="absolute inset-0 w-full h-full object-cover object-center block"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={handlePrev}
          className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center border border-[#dddddd] bg-white hover:scale-110 transition-transform duration-200"
          aria-label="Previous banner"
        >
          <img src={leftIcon} className="w-6 h-6" alt="Previous" />
        </button>

        <button
          onClick={handleNext}
          className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center bg-white/90 backdrop-blur-sm hover:scale-110 transition-transform duration-200"
          aria-label="Next banner"
        >
          <img src={rightIcon} className="w-6 h-6" alt="Next" />
        </button>
      </div>

      <div className="w-full h-0.5 relative">
        <div className="w-full h-0.5 left-0 top-0 absolute bg-[#eeeeee]">
          <div 
            className="h-0.5 bg-[#111111]"
            style={{ 
              width: bannerCount ? `${100 / bannerCount}%` : '0%',
              transform: `translateX(${currentIndex * 100}%)`
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default BannerSection; 
